import {
  BitkitTreeView,
  type BitkitTreeViewExpandedChangeDetails,
  type BitkitTreeViewNodeRenderProps,
  type BitkitTreeViewRootProps,
  type BitkitTreeViewSelectionChangeDetails,
  createTreeCollection,
  IconDetails,
} from '@bitrise/bitkit-v2';
import difference from 'lodash/difference';
import { type KeyboardEvent, useMemo, useRef } from 'react';

import { formatDuration } from '@/utils/commonFormats';

import { trackTestSuiteDetailsExpanded } from '../TestResults.tracking';
import { TestCase, TestFilter, TestFilters, TestSuite } from '../TestResults.types';
import { collectTestSuiteIds, mapTestCaseStatusToIcon } from './TestResultsExplorer.utils';

type Props = {
  suites: TestSuite[];
  filters: TestFilters;
  onTestCaseClick: (id: string, testCase: TestCase) => void;
  onTestSuiteDetailsClick: (id: string, testSuite: TestSuite) => void;
};

type TreeNode =
  | { type: 'root'; id: string; children: TreeNode[] }
  | { type: 'suite'; id: string; path: string; testSuite: TestSuite; children: TreeNode[] }
  | { type: 'details'; id: string; path: string; testSuite: TestSuite }
  | { type: 'testCase'; id: string; path: string; testCase: TestCase };

function formatTestDuration(durationInMs?: number): string | undefined {
  if (typeof durationInMs === 'number') {
    return formatDuration(durationInMs / 1000);
  }
  return undefined;
}

function getSuiteLabel(suite: TestSuite, statusFilter?: TestFilter): string | undefined {
  const suffix = suite.testCount !== undefined ? `/${suite.testCount}` : '';

  // Helper function to recursively count tests
  function countRecursive(currentSuite: TestSuite, predicate: (test: TestCase) => boolean): number {
    const caseCount = currentSuite.testCases?.filter(predicate).length || 0;
    const suiteCount =
      currentSuite.testSuites?.reduce((sum, subSuite) => sum + countRecursive(subSuite, predicate), 0) || 0;
    return caseCount + suiteCount;
  }

  let label;

  switch (statusFilter) {
    case 'failed':
      label = suite.failureCount ? `${suite.failureCount}${suffix} Failed` : undefined;
      break;
    case 'skipped':
      label = suite.skippedCount !== undefined ? `${suite.skippedCount}${suffix} Skipped` : undefined;
      break;
    case 'error':
      label = suite.errorCount !== undefined ? `${suite.errorCount}${suffix} Errors` : undefined;
      break;
    case 'flaky':
      label = `${countRecursive(suite, (test) => Boolean(test.flaky))}${suffix} Flaky`;
      break;
    case 'passed': {
      label = `${countRecursive(suite, (test) => test.status === 'passed')}${suffix} Passed`;
      break;
    }
    default:
      label = typeof suite.testCount === 'number' ? `${suite.testCount} Tests` : undefined;
      break;
  }

  return [label, formatTestDuration(suite.durationInMs)].filter(Boolean).join(' • ') || undefined;
}

function toSuiteNode(testSuite: TestSuite, path: string): TreeNode {
  const detailsNodes: TreeNode[] = testSuite.attachments?.length
    ? [{ type: 'details', id: `${testSuite.id}-details`, path, testSuite }]
    : [];
  const testCaseNodes: TreeNode[] = (testSuite.testCases ?? []).map((testCase) => ({
    type: 'testCase',
    id: testCase.id,
    path,
    testCase,
  }));
  const subSuiteNodes = (testSuite.testSuites ?? []).map((subSuite) =>
    toSuiteNode(subSuite, [path, subSuite.name].filter(Boolean).join(' / ')),
  );

  return {
    type: 'suite',
    id: testSuite.id,
    path,
    testSuite,
    children: [...detailsNodes, ...testCaseNodes, ...subSuiteNodes],
  };
}

function getNodeName(node: TreeNode): string {
  switch (node.type) {
    case 'root':
      return '';
    case 'suite':
      return node.testSuite.name;
    case 'details':
      return 'Test run details';
    case 'testCase':
      return node.testCase.name;
  }
}

type ExpandTrackingTreeViewProps = Omit<BitkitTreeViewRootProps, 'defaultExpandedValue' | 'onExpandedChange'> & {
  defaultExpandedValue: string[];
};

// Keyed by the caller, so each remount starts the tracking from its own defaultExpandedValue.
const ExpandTrackingTreeView = ({ defaultExpandedValue, ...props }: ExpandTrackingTreeViewProps) => {
  const lastExpanded = useRef(defaultExpandedValue);

  const handleExpandedChange = ({ expandedValue }: BitkitTreeViewExpandedChangeDetails) => {
    difference(expandedValue, lastExpanded.current).forEach(() => trackTestSuiteDetailsExpanded());
    lastExpanded.current = expandedValue;
  };

  return (
    <BitkitTreeView {...props} defaultExpandedValue={defaultExpandedValue} onExpandedChange={handleExpandedChange} />
  );
};

const TestResultsTree = ({ suites, filters, onTestCaseClick, onTestSuiteDetailsClick }: Props) => {
  const collection = useMemo(
    () =>
      createTreeCollection<TreeNode>({
        rootNode: { type: 'root', id: 'root', children: suites.map((suite) => toSuiteNode(suite, suite.name)) },
        nodeToValue: (node) => node.id,
        nodeToString: getNodeName,
      }),
    [suites],
  );

  const defaultExpandedValue = useMemo(() => {
    const { status } = filters;
    if (status === 'all' || status === 'passed') {
      return [];
    }
    return Array.from(collectTestSuiteIds(suites));
  }, [suites, filters.status]);

  // NOTE: This is a workaround to force remounting the TreeView when the defaultExpandedValue changes.
  const key = defaultExpandedValue.join(',');

  const openNode = (node?: TreeNode) => {
    if (node?.type === 'testCase') {
      onTestCaseClick(node.path, node.testCase);
    }
    if (node?.type === 'details') {
      onTestSuiteDetailsClick(node.path, node.testSuite);
    }
  };

  const handleSelectionChange = ({ selectedValue }: BitkitTreeViewSelectionChangeDetails) => {
    openNode(collection.findNode(selectedValue[0]));
  };

  const renderNode = ({ node, nodeState }: BitkitTreeViewNodeRenderProps) => {
    const treeNode = node as TreeNode;
    // Activating the selected leaf again does not change the selection, so onSelectionChange never fires for it.
    const reselectProps = nodeState.selected
      ? {
          onClick: () => openNode(treeNode),
          onKeyDown: (event: KeyboardEvent) => (event.key === 'Enter' || event.key === ' ') && openNode(treeNode),
        }
      : {};

    switch (treeNode.type) {
      case 'suite':
        return (
          <BitkitTreeView.Branch
            label={treeNode.testSuite.name}
            subtext={treeNode.testSuite.properties
              ?.map((p) => p.value)
              .filter(Boolean)
              .join(' • ')}
            suffixText={getSuiteLabel(treeNode.testSuite, filters.status)}
          />
        );
      case 'details':
        return <BitkitTreeView.Leaf {...reselectProps} icon={IconDetails} label="Test run details" />;
      case 'testCase':
        return (
          <BitkitTreeView.Leaf
            {...reselectProps}
            {...mapTestCaseStatusToIcon(treeNode.testCase.status)}
            hasWarning={treeNode.testCase.flaky}
            label={treeNode.testCase.name}
            suffixText={formatTestDuration(treeNode.testCase.durationInMs)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ExpandTrackingTreeView
      key={key}
      collection={collection}
      defaultExpandedValue={defaultExpandedValue}
      lazyMount
      onSelectionChange={handleSelectionChange}
      render={renderNode}
      translations={{ treeLabel: 'Test Results' }}
      unmountOnExit
      variant="navigation"
    />
  );
};

export default TestResultsTree;
